import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Empty,
  LayerCard,
  LinkButton,
  Loader,
  Pagination,
  Table,
  Text,
  useKumoToastManager,
  useSidebar,
} from '@cloudflare/kumo'
import { HouseIcon, CalendarBlankIcon, PlusIcon } from '@phosphor-icons/react'
import type { RoomBookingDto } from '@hub-wifi/shared'
import { apiRequest, ApiError } from '../api/client'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { formatDateTime, formatDuration } from '../lib/format'
import { onPlainClick } from '../components/home/plain-click'
import './my-bookings.css'

type Timeframe = 'upcoming' | 'past'

interface ListResp {
  items: RoomBookingDto[]
  total: number
  page: number
  limit: number
}

const PER_PAGE = 15

/** Translate booking API error to a user-facing string (shared with BookingFormPage). */
function describeBookingError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { code?: string; message?: string }
    const code = body.code ?? err.message
    const translated = t(`bookings.errors.${code}`)
    if (translated && translated !== `bookings.errors.${code}`) return translated
    if (body.message) return body.message
    return code || t('common.error')
  }
  return t('common.error')
}

function bookingMinutes(b: RoomBookingDto): number {
  return (new Date(b.endAt).getTime() - new Date(b.startAt).getTime()) / 60000
}

/** Phone layout: one row per booking, actions as 44px buttons. */
function BookingCards({
  bookings,
  past,
  cancellingId,
  onCancel,
}: {
  bookings: RoomBookingDto[]
  past: boolean
  cancellingId: string | null
  onCancel: (b: RoomBookingDto) => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const go = (href: string) => onPlainClick(() => navigate(href))
  return (
    <LayerCard>
      <ul
        className="my-bookings-cards"
        data-testid={`my-bookings-${past ? 'past' : 'upcoming'}-cards`}
      >
        {bookings.map((b) => {
          const edit = `/my/bookings/${encodeURIComponent(b.id)}/edit`
          const invite = `/my/guests/new?roomBookingId=${encodeURIComponent(b.id)}`
          return (
            <li key={b.id} className="my-bookings-card" data-testid={`my-booking-card-${b.id}`}>
              <div className="my-bookings-card__head">
                <Text variant="body" as="span">
                  {b.roomName ?? b.roomId}
                </Text>
                {past && (
                  <Badge variant={b.status === 'confirmed' ? 'primary' : 'secondary'}>
                    {t(`bookings.status.${b.status}`)}
                  </Badge>
                )}
              </div>
              <span className="my-bookings-card__meta">
                {formatDateTime(b.startAt)} · {formatDuration(bookingMinutes(b), t)}
              </span>
              {!past && (
                <div className="my-bookings-card__actions">
                  <LinkButton href={invite} onClick={go(invite)} variant="primary" size="lg">
                    {t('myBookings.inviteGuest')}
                  </LinkButton>
                  <LinkButton href={edit} onClick={go(edit)} variant="secondary" size="lg">
                    {t('myBookings.reschedule')}
                  </LinkButton>
                  <Button
                    variant="secondary-destructive"
                    size="lg"
                    loading={cancellingId === b.id}
                    disabled={cancellingId !== null}
                    onClick={() => onCancel(b)}
                  >
                    {t('bookings.cancel')}
                  </Button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </LayerCard>
  )
}

export function MyBookingsPage() {
  const { getAccessTokenSilently } = useAuth0()
  // Same breakpoint as the phone tab bar; only one layout is ever in the DOM.
  const { isMobile } = useSidebar()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [items, setItems] = useState<RoomBookingDto[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [cancelTarget, setCancelTarget] = useState<RoomBookingDto | null>(null)
  const [activeTab, setActiveTab] = useState<Timeframe>('upcoming')
  // Drops responses from superseded requests when the tab/page changes fast.
  const requestSeq = useRef(0)

  // The API filters and orders per tab: upcoming soonest-first (so today's
  // meeting leads page 1), past most-recent-first.
  async function load() {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const qs = new URLSearchParams({
        timeframe: activeTab,
        page: String(page),
        limit: String(PER_PAGE),
      })
      const resp = await apiRequest<ListResp>(`/api/my/bookings?${qs}`, { token })
      if (seq !== requestSeq.current) return
      // Cancelling the last row of the last page leaves it empty: step back.
      if (resp.items.length === 0 && page > 1) {
        setPage(page - 1)
        return
      }
      setItems(resp.items)
      setTotal(resp.total)
    } catch (err) {
      if (seq !== requestSeq.current) return
      setError(describeBookingError(err, t))
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page])

  async function confirmCancel() {
    if (!cancelTarget) return
    const id = cancelTarget.id
    setCancellingId(id)
    try {
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/my/bookings/${id}/cancel`, {
        method: 'POST',
        token,
        body: {},
      })
      toasts.add({ variant: 'success', title: t('bookings.cancelSuccess') })
      setCancelTarget(null)
      await load()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: describeBookingError(err, t),
      })
    } finally {
      setCancellingId(null)
    }
  }

  const upcoming = activeTab === 'upcoming' ? items : []
  const past = activeTab === 'past' ? items : []

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('myBookings.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('myBookings.title')}
        description={t('myBookings.pageDescription')}
        tabs={[
          { label: t('myBookings.upcoming'), value: 'upcoming' },
          { label: t('myBookings.past'), value: 'past' },
        ]}
        defaultTab={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as Timeframe)
          setPage(1)
          // Show the loader, not the previous tab's rows, until the fetch lands.
          setLoading(true)
        }}
      >
        <Link to="/my/bookings/new">
          <Button variant="primary" size="sm">
            {t('myBookings.book')}
          </Button>
        </Link>
      </PageHeader>

      {error && (
        <Banner variant="error" className="mb-3">
          {t('common.errorPrefix')} {error}
        </Banner>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && (
        <>
          {activeTab === 'upcoming' &&
            (upcoming.length === 0 ? (
              <Empty
                icon={<CalendarBlankIcon size={48} className="text-kumo-inactive" />}
                title={t('myBookings.empty')}
                contents={
                  <Link to="/my/bookings/new">
                    <Button variant="primary" size="sm" icon={<PlusIcon size={16} />}>
                      {t('myBookings.book')}
                    </Button>
                  </Link>
                }
              />
            ) : isMobile ? (
              <BookingCards
                bookings={upcoming}
                past={false}
                cancellingId={cancellingId}
                onCancel={setCancelTarget}
              />
            ) : (
              <LayerCard>
                <LayerCard.Primary className="p-0">
                  <div style={{ overflowX: 'auto', width: '100%' }}>
                    <Table data-testid="my-bookings-upcoming">
                      <Table.Header>
                        <Table.Row>
                          <Table.Head>{t('bookings.col.when')}</Table.Head>
                          <Table.Head>{t('bookings.col.duration')}</Table.Head>
                          <Table.Head>{t('bookings.col.room')}</Table.Head>
                          <Table.Head>{t('bookings.col.actions')}</Table.Head>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {upcoming.map((b) => (
                          <Table.Row key={b.id} data-testid={`my-booking-row-${b.id}`}>
                            <Table.Cell>{formatDateTime(b.startAt)}</Table.Cell>
                            <Table.Cell>{formatDuration(bookingMinutes(b), t)}</Table.Cell>
                            <Table.Cell>{b.roomName ?? b.roomId}</Table.Cell>
                            <Table.Cell>
                              <div className="flex gap-2">
                                <Link to={`/my/bookings/${b.id}/edit`}>
                                  <Button variant="ghost" size="sm">
                                    {t('myBookings.reschedule')}
                                  </Button>
                                </Link>
                                <Link to={`/my/guests/new?roomBookingId=${b.id}`}>
                                  <Button variant="ghost" size="sm">
                                    {t('myBookings.inviteGuest')}
                                  </Button>
                                </Link>
                                <Button
                                  variant="secondary-destructive"
                                  size="sm"
                                  loading={cancellingId === b.id}
                                  disabled={cancellingId !== null}
                                  onClick={() => setCancelTarget(b)}
                                >
                                  {t('bookings.cancel')}
                                </Button>
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </LayerCard.Primary>
              </LayerCard>
            ))}

          {activeTab === 'past' &&
            (past.length === 0 ? (
              <Empty
                icon={<CalendarBlankIcon size={48} className="text-kumo-inactive" />}
                title={t('myBookings.emptyPast')}
              />
            ) : isMobile ? (
              <BookingCards
                bookings={past}
                past
                cancellingId={cancellingId}
                onCancel={setCancelTarget}
              />
            ) : (
              <LayerCard>
                <LayerCard.Primary className="p-0">
                  <div style={{ overflowX: 'auto', width: '100%' }}>
                    <Table data-testid="my-bookings-past">
                      <Table.Header>
                        <Table.Row>
                          <Table.Head>{t('bookings.col.when')}</Table.Head>
                          <Table.Head>{t('bookings.col.duration')}</Table.Head>
                          <Table.Head>{t('bookings.col.room')}</Table.Head>
                          <Table.Head>{t('bookings.col.status')}</Table.Head>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {past.map((b) => (
                          <Table.Row key={b.id}>
                            <Table.Cell>{formatDateTime(b.startAt)}</Table.Cell>
                            <Table.Cell>{formatDuration(bookingMinutes(b), t)}</Table.Cell>
                            <Table.Cell>{b.roomName ?? b.roomId}</Table.Cell>
                            <Table.Cell>
                              <Badge variant={b.status === 'confirmed' ? 'primary' : 'secondary'}>
                                {t(`bookings.status.${b.status}`)}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </LayerCard.Primary>
              </LayerCard>
            ))}

          {total > 0 && (
            <div className="mt-2">
              <Pagination page={page} setPage={setPage} perPage={PER_PAGE} totalCount={total}>
                <Pagination.Info>
                  {({ pageShowingRange }) =>
                    t('myBookings.paginationInfo', {
                      range: pageShowingRange,
                      total: String(total),
                    })
                  }
                </Pagination.Info>
                <Pagination.Controls />
              </Pagination>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(next) => {
          if (!next) setCancelTarget(null)
        }}
        title={t('bookings.confirmCancel')}
        description={t('bookings.confirmCancelBody')}
        confirmLabel={t('bookings.confirmCancelAction')}
        cancelLabel={t('common.back')}
        loading={cancellingId !== null}
        onConfirm={() => void confirmCancel()}
      />
    </div>
  )
}
